import { useState, type ReactNode } from 'react'
import { Link, useLocation, useMatch } from 'react-router-dom'
import { AppShell, Header, RequestIndicator, ThemeToggle, ToastViewport, useApiQuery, useAuth } from 'grabbitbot-legacy-ui'
import { apiClient } from '../api/client'
import { projects } from '../api/fee'
import type { Project } from '../api/types'
import { APP_TITLE } from '../config'
import { useFinanceOptional } from '../admin-finance/financeContext'

interface MenuItem {
  label: string
  to: string
  heading?: boolean
}

/** One header dropdown — port of `app-dropdown-menu` (closes on a pick,
 * and — unlike legacy — also on Escape / focus leaving the menu). */
function Menu({ label, items, active }: { label: string; items: MenuItem[]; active: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="fs-dropdown"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        className={active ? 'fs-nav-button fs-nav-button--active' : 'fs-nav-button'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        {label} ▾
      </button>
      {open && (
        <div className="fs-dropdown__panel" role="menu" aria-label={label}>
          <ul>
            {items.map((item) =>
              item.heading ? (
                <li key={`h-${item.label}`} className="fs-dropdown__heading">
                  {item.label}
                </li>
              ) : (
                <li key={item.to}>
                  <Link role="menuitem" to={item.to} onClick={() => setOpen(false)}>
                    {item.label}
                  </Link>
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Felhasználó | Admin switch — shown only when the finance scope says the
 * viewer is the global admin or administers at least one project. */
function ModeSwitch() {
  const fin = useFinanceOptional()
  if (!fin || !fin.visible) return null
  return (
    <div className="af-seg af-seg--mode" role="group" aria-label="Nézet mód">
      <button type="button" aria-pressed={fin.mode === 'user'} className={fin.mode === 'user' ? 'on' : ''} onClick={() => fin.mode !== 'user' && fin.setMode('user')}>
        Felhasználó
      </button>
      <button type="button" aria-pressed={fin.mode === 'admin'} className={fin.mode === 'admin' ? 'on' : ''} onClick={() => fin.mode !== 'admin' && fin.setMode('admin')}>
        Admin
      </button>
    </div>
  )
}

function projectMenus(project: Project, pathname: string) {
  const base = `/projects/${encodeURIComponent(project.id)}`
  const isUser = Boolean(project.isUser)
  const isAdmin = Boolean(project.isAdmin)
  const payment: MenuItem[] = [
    { label: 'Subscriptions', to: `${base}/subscriptions` },
    { label: 'Products', to: `${base}/products` },
    { label: 'Usage Fees', to: `${base}/usagefees` },
    ...(isUser ? [{ label: 'Payment method', to: `${base}/payment/method` }] : []),
    { label: 'Billing', to: `${base}/payment/billing` },
    ...(isUser
      ? [
          { label: 'Credits', to: `${base}/payment/credit` },
          { label: 'Transactions', to: `${base}/payment/transactions` },
          { label: 'Invoices', to: `${base}/payment/invoices` },
        ]
      : []),
  ]
  const config: MenuItem[] = [
    { label: 'User Access', to: `${base}/users` },
    { label: 'Project Config', to: `${base}/edit` },
    { label: 'Service Accounts', to: `${base}/service-accounts` },
    { label: 'Telegram', to: '', heading: true },
    { label: 'Telegram Chat User Manager', to: `${base}/telegram-chat-user-manager` },
  ]
  const admin: MenuItem[] = [
    { label: 'Users', to: `${base}/admin/users` },
    { label: 'Plans', to: `${base}/admin/plans` },
    { label: 'Products', to: `${base}/admin/products` },
    { label: 'Credits', to: `${base}/admin/credits` },
    { label: 'Invoices', to: `${base}/admin/invoices` },
  ]
  const rest = pathname.slice(base.length)
  const section = rest.startsWith('/admin')
    ? 'admin'
    : /^\/(users|edit|service-accounts|telegram-chat-user-manager)/.test(rest)
      ? 'config'
      : /^\/(subscriptions|products|usagefees|payment)/.test(rest)
        ? 'payment'
        : rest === '' || rest === '/'
          ? 'dashboard'
          : ''
  return { base, payment, config, admin, isAdmin, section }
}

/**
 * Port of the legacy `HeaderComponent` + `ProjectBreadcrumbComponent` +
 * `PageContainerComponent`. Inside a project (`/projects/:projectId/*`):
 * Dashboard, Payment, and — `project.isAdmin` only — Config and Admin
 * menus, exactly the legacy menu tree. The user menu keeps the legacy
 * "Connections" section (Settings for the current project, Telegram).
 * Plus the fleet additions: theme toggle, request indicator.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const match = useMatch('/projects/:projectId/*')
  const projectId = match?.params.projectId
  const projectQ = useApiQuery(
    () => (projectId ? projects.view(projectId) : Promise.resolve(null)),
    [projectId],
  )
  const project = projectId && projectQ.data && projectQ.data.id ? projectQ.data : null
  const menus = project ? projectMenus(project, location.pathname) : null
  const adminMode = useFinanceOptional()?.mode === 'admin'

  const userItems: MenuItem[] = [
    { label: 'Connections', to: '', heading: true },
    ...(project ? [{ label: 'Settings', to: `/projects/${encodeURIComponent(project.id)}/connection-settings` }] : []),
    { label: 'Telegram', to: '/connections/telegram' },
    { label: 'Log out', to: '/logout' },
  ]

  return (
    <AppShell
      header={
        user ? (
          <Header
            title={<Link to="/projects">{APP_TITLE}</Link>}
            nav={
              <nav className="portal-nav fs-nav" aria-label="Main">
                {adminMode ? (
                  <span className="fs-crumb fs-crumb--project">Admin · pénzügy</span>
                ) : (
                  <Link to="/projects" className="fs-crumb">
                    Projects
                  </Link>
                )}
                {!adminMode && project && menus && (
                  <>
                    <span className="fs-crumb-sep" aria-hidden="true">
                      /
                    </span>
                    <Link to={menus.base} className="fs-crumb fs-crumb--project">
                      {project.name || project.id}
                    </Link>
                    <Link
                      to={menus.base}
                      className={menus.section === 'dashboard' ? 'fs-nav-button fs-nav-button--active' : 'fs-nav-button'}
                    >
                      Dashboard
                    </Link>
                    <Menu label="Payment" items={menus.payment} active={menus.section === 'payment'} />
                    {menus.isAdmin && <Menu label="Config" items={menus.config} active={menus.section === 'config'} />}
                    {menus.isAdmin && <Menu label="Admin" items={menus.admin} active={menus.section === 'admin'} />}
                  </>
                )}
              </nav>
            }
            actions={
              <div className="portal-actions">
                <RequestIndicator client={apiClient} />
                <ModeSwitch />
                <ThemeToggle />
                <Menu label={user.username} items={userItems} active={location.pathname.startsWith('/connections')} />
              </div>
            }
          />
        ) : undefined
      }
    >
      {children}
      <ToastViewport />
    </AppShell>
  )
}
